import { registerTranslationRefresh, t as lnT } from '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaEye, FaPencilAlt } from 'react-icons/fa';

import AuthLayout from '../AuthLayout';
import environment from '../../../env-config.js';
import { OAuthSessionExpiredError, redirectToOAuthLogin, requestOAuthConsent } from './oauthSession';
import * as COLOR from '../../../appearance/Colors';

/**
 * Layer 2 remote-MCP OAuth2 consent screen — served at /oauth/consent, the
 * client-side React route oidc-provider's `interactions.url` redirects the
 * browser to (see server/src/modules/oauth/provider-config.ts).
 *
 * Renders one list item per scope actually being requested for this
 * interaction (structurally modeled on Fabric's consent screen — icon,
 * bold title, one-line description, per granted permission — but keeping
 * Luckynote's own colors/fonts rather than Fabric's dark theme). Explicit
 * divergence from Fabric, per the MCP write-scope plan
 * (docs/mastermind/mcp-write-scope-plan-2026-08-01.md): Fabric bundles
 * "read and write" into one unbundle-able grant with no visibility into
 * what's actually being allowed. Luckynote never does that — each scope
 * the server says is being requested (`details.scopes`, from
 * oauth.controller.ts's getConsentDetails) gets its own distinct,
 * honestly-labeled row, including a "cannot X" line so users know the
 * limits, not just the grant.
 *
 * The server sends raw scope identifiers ('read' | 'write'), not
 * pre-formatted copy — SCOPE_COPY below owns the human-readable
 * title/description, consistent with how this codebase keeps UI copy as a
 * frontend concern elsewhere (ApiTokensComponent.js renders raw scope
 * strings from the server today too, just without title/description copy
 * since token management is a simpler internal-facing surface than this
 * consent screen).
 *
 * `details.scopes` is rendered exactly as the server reports it, with no
 * fallback: an earlier version of this page defaulted to `['read']`
 * whenever the array came back empty, written back when no write-capable
 * MCP tool existed and 'write' could never actually be requested. Now that
 * create_message/create_folder/move_message/rename_folder exist, that
 * fallback would silently mislabel a write-scope grant as read-only — so an
 * empty/missing list here must render as "no scopes" (see the empty-state
 * check below), never a guessed default.
 */
const makeTranslatedSCOPE_COPY = () => ({
    read: {
        icon: FaEye,
        title: lnT('auth:readAccessToYourNotes'),
        description: lnT('auth:viewYourFoldersAndNotesCannotCreate')
    },
    write: {
        icon: FaPencilAlt,
        title: lnT('auth:createAndEditYourNotes'),
        description:
            lnT('settings:createNewNotesAndEditOrMove')
    }
});
let SCOPE_COPY = makeTranslatedSCOPE_COPY();
registerTranslationRefresh(() => { SCOPE_COPY = makeTranslatedSCOPE_COPY(); });

const makeTranslatedDEFAULT_SCOPE_COPY = () => ({
    icon: FaEye,
    title: lnT('auth:accessToYourNotes'),
    description: lnT('auth:requestedAccessForThisConnection')
});
let DEFAULT_SCOPE_COPY = makeTranslatedDEFAULT_SCOPE_COPY();
registerTranslationRefresh(() => { DEFAULT_SCOPE_COPY = makeTranslatedDEFAULT_SCOPE_COPY(); });
// REACT_BACKEND_URL is the GraphQL endpoint (e.g. http://localhost:4000/graphql).
// The OAuth REST endpoints (/oauth/consent-details, etc.) live at the API
// origin's root, not under /graphql, so strip that suffix before using it
// as a base for these fetches.
const apiUrl = (environment.REACT_BACKEND_URL || '').replace(/\/graphql\/?$/, '');

const OAuthConsentPage = () => {
    const {t: lnT} = useTranslation();

    const location = useLocation();
    const [details, setDetails] = useState(null);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const uid = new URLSearchParams(location.search).get('uid');

    const respond = useCallback(
        async approve => {
            setSubmitting(true);
            setError('');
            try {
                const res = await requestOAuthConsent(
                    `${apiUrl}/oauth/consent-${approve ? 'approve' : 'deny'}?uid=${encodeURIComponent(uid)}`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        // See the consent-details fetch below: needed for oidc-provider's
                        // interaction cookie to be sent cross-origin.
                        credentials: 'include'
                    }
                );
                const body = await res.json();
                if (!res.ok || !body.redirectTo) {
                    throw new Error(body?.error_description || body?.error || 'Something went wrong.');
                }
                // redirectTo comes back from oidc-provider (via the server) as a
                // path relative to the API server's origin (e.g.
                // /oauth/authorize/... to resume the interaction) — NOT the
                // client app's own origin this page is running on. Resolving a
                // bare path with window.location.href would send the browser
                // back to a nonexistent route on the client app instead of the
                // API server, the same class of bug as the initial consent
                // redirect (see provider-config.ts's OAUTH_CONSENT_ROUTE).
                const absoluteRedirect = /^https?:\/\//i.test(body.redirectTo)
                    ? body.redirectTo
                    : `${apiUrl}${body.redirectTo}`;
                window.location.href = absoluteRedirect;
            } catch (err) {
                if (err instanceof OAuthSessionExpiredError) {
                    redirectToOAuthLogin(location.search, true);
                    return;
                }
                setSubmitting(false);
                setError(err.message || lnT('auth:somethingWentWrong'));
            }
        },
        [lnT, uid, location.search]
    );

    useEffect(() => {
        if (!uid) {
            setError(lnT('auth:missingOrInvalidAuthorizationRequest'));
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            redirectToOAuthLogin(location.search, true);
            return;
        }

        requestOAuthConsent(`${apiUrl}/oauth/consent-details?uid=${encodeURIComponent(uid)}`, {
            // Required so the browser sends oidc-provider's `_interaction` session cookie
            // cross-origin (client app origin -> API origin); the server pairs this with an
            // explicit (non-wildcard) Access-Control-Allow-Origin + Allow-Credentials for
            // these specific /oauth/consent-* routes (see server/src/index.ts).
            credentials: 'include'
        })
            .then(async res => {
                const body = await res.json();
                if (!res.ok) {
                    throw new Error(body?.error_description || body?.error || 'Failed to load request.');
                }
                // oidc-provider can only compute resource-scope (read/write)
                // missingness once a Grant already exists for this
                // client+account — on a first-ever authorization there isn't
                // one yet, so this initial pass can legitimately report zero
                // scopes even though a real read/write prompt is coming right
                // after. Auto-approving this empty, nothing-to-decide pass
                // (instead of rendering a screen with no options and making
                // the user click through it) collapses what would otherwise
                // be two consent screens back down to the one that actually
                // has something to approve.
                if (Array.isArray(body?.scopes) && body.scopes.length === 0) {
                    respond(true);
                    return;
                }
                setDetails(body);
            })
            .catch(err => {
                if (err instanceof OAuthSessionExpiredError) {
                    redirectToOAuthLogin(location.search, true);
                    return;
                }
                setError(err.message || lnT('auth:failedToLoadAuthorizationRequest'));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);

    return (
        <AuthLayout>
            <div style={{ padding: '8px 4px', textAlign: 'center' }}>
                <div style={{ margin: '0 0 8px', color: '#23272a', fontSize: 22, fontWeight: 600, lineHeight: 1.3 }}>
                    {details ? lnT('auth:wantsToConnect', { value1: String(details.clientName) }) : lnT('auth:connectAnApp')}
                </div>

                {error ? (
                    <div
                        style={{
                            color: COLOR.RED,
                            background: 'rgba(255,0,0,0.08)',
                            borderRadius: 8,
                            padding: '10px 14px',
                            margin: '12px 0',
                            fontSize: 14
                        }}
                    >
                        {error}
                    </div>
                ) : null}

                {!details && !error ? (
                    <p style={{ color: '#666' }}>{lnT('auth:loadingRequest')}</p>
                ) : null}

                {details ? (
                    <>
                        <p style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.5 }}>
                            {details.scopes?.length ? (
                                <>{lnT('auth:thisWillLet')}<strong>{details.clientName}</strong>{lnT('auth:accessLuckynoteWithThePermissionsBelow')}</>
                            ) : (
                                <>{lnT('auth:thisWillLet')}<strong>{details.clientName}</strong>{lnT('auth:connectToLuckynoteItAlreadyHasThe')}</>
                            )}
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 14,
                                margin: '18px 0',
                                textAlign: 'left'
                            }}
                        >
                            {(details.scopes ?? []).map(scope => {
                                const copy = SCOPE_COPY[scope] || DEFAULT_SCOPE_COPY;
                                const Icon = copy.icon;
                                return (
                                    <div
                                        key={scope}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 12
                                        }}
                                    >
                                        <div
                                            style={{
                                                flexShrink: 0,
                                                width: 36,
                                                height: 36,
                                                borderRadius: 8,
                                                background: COLOR.GREEN_OP_1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Icon size={16} color={COLOR.GREEN} />
                                        </div>
                                        <div>
                                            <div
                                                style={{
                                                    color: '#23272a',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {copy.title}
                                            </div>
                                            <div
                                                style={{
                                                    color: '#757575',
                                                    fontSize: 13,
                                                    lineHeight: 1.4,
                                                    marginTop: 2
                                                }}
                                            >
                                                {copy.description}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {details.clientId ? (
                            <p style={{ color: '#757575', fontSize: 12, opacity: 0.7 }}>
                                {details.clientId}
                            </p>
                        ) : null}

                        <div
                            style={{
                                display: 'flex',
                                gap: 12,
                                justifyContent: 'center',
                                marginTop: 24
                            }}
                        >
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => respond(false)}
                                style={{
                                    padding: '10px 22px',
                                    borderRadius: 8,
                                    border: '1px solid #ccc',
                                    background: 'transparent',
                                    color: '#23272a',
                                    cursor: submitting ? 'default' : 'pointer'
                                }}
                            >{lnT('auth:deny')}</button>
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => respond(true)}
                                style={{
                                    padding: '10px 22px',
                                    borderRadius: 8,
                                    border: 'none',
                                    background: COLOR.GREEN,
                                    color: COLOR.WHITE,
                                    cursor: submitting ? 'default' : 'pointer'
                                }}
                            >{lnT('auth:approve')}</button>
                        </div>
                    </>
                ) : null}
            </div>
        </AuthLayout>
    );
};

export default OAuthConsentPage;
